import React from 'react';
import { FileTextIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { CtaBand } from '../components/ui/CtaBand';
import { achievements } from '../data/record';
import { timeline } from '../data/candidate';
import { candidate } from '../data/site';
const supportingData = [{
  value: '4.1x',
  label: 'Growth in Osun internally generated revenue, 2015–2023'
}, {
  value: '84',
  label: 'Consecutive months of salaries paid on or before the 25th'
}, {
  value: '1,240',
  label: 'Classrooms rebuilt across the state'
}, {
  value: '312',
  label: 'Primary healthcare centres revitalised'
}, {
  value: '38% → 9%',
  label: 'Non-performing loans at the industrial bank'
}, {
  value: '22,000',
  label: 'Smallholder farmers under contract offtake'
}];
const documents = ['Osun State Accountant-General Report, 2023', 'Osun State Handover Audit, June 2023', 'NIDB Annual Report, 2015', 'Independent impact audit, Dalberg 2012', 'Auditor-General for the Federation, agency reviews'];
export function TrackRecord() {
  return <>
      <PageHero eyebrow="Track record" title="Judge the plan by the record." intro="Three decades of results, each one tied to a public document you can check independently." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Track Record'
    }]} image={candidate.portraitSigning} imageAlt={`${candidate.name} signing an agreement`} data-id="element-1453" data-mp="24q3t3wuyov10" />

      <section className="bg-sand-50 py-16 lg:py-24" aria-labelledby="cases-heading" data-id="element-1454" data-mp="2ngbrk1npuxav">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1455" data-mp="2nifw8ltfw0c7">
          <SectionHeading eyebrow="Case studies" title="Challenge, action, result — with sources" intro="The same structure used for every claim on this website." data-id="element-1456" data-mp="2sdv3uw0ut006" />
          <div className="mt-12 space-y-6" data-id="element-1457" data-mp="1h1kk1f438e7v">
            {achievements.map((item, index) => <Reveal key={item.role} delay={Math.min(index, 3) * 0.04} data-id="element-1458" data-mp="wxda2l5ymk4i" data-mpi={index}>
                <article className="grid gap-7 rounded-media border border-sand-200 bg-white p-7 lg:grid-cols-12 lg:p-10" data-id="element-1459" data-mp="sxmdu1xnzbsl">
                  <header className="lg:col-span-4" data-id="element-1460" data-mp="o408yvxqsc7c">
                    <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-green-700" data-id="element-1461" data-mp="j79w0hh8hwq5">
                      {item.period}
                    </p>
                    <h3 className="mt-2 font-display text-[24px] leading-tight text-ink" data-id="element-1462" data-mp="e5kh2qqw4yws">
                      {item.role}
                    </h3>
                    <p className="mt-4 flex items-start gap-2 text-[13px] leading-relaxed text-slate-500" data-id="element-1463" data-mp="5677zz6oiccl">
                      <FileTextIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1464" data-mp="8xiykn7c2pa" />
                      {item.source}
                    </p>
                  </header>
                  <dl className="space-y-5 lg:col-span-8" data-id="element-1465" data-mp="1um6nrz4sz017">
                    <div className="border-l-2 border-sand-300 pl-5" data-id="element-1466" data-mp="1umoslk5j6mkq">
                      <dt className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500" data-id="element-1467" data-mp="224mvv6x4d1fi">
                        Challenge
                      </dt>
                      <dd className="mt-1.5 text-[15px] leading-relaxed text-slate-700" data-id="element-1468" data-mp="2bvhlml9kqj5p">
                        {item.challenge}
                      </dd>
                    </div>
                    <div className="border-l-2 border-sand-300 pl-5" data-id="element-1469" data-mp="2gw5bv4pzavyv">
                      <dt className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500" data-id="element-1470" data-mp="1j8en3t4we8fg">
                        Action
                      </dt>
                      <dd className="mt-1.5 text-[15px] leading-relaxed text-slate-700" data-id="element-1471" data-mp="1t4hzdrtgrng4">
                        {item.action}
                      </dd>
                    </div>
                    <div className="border-l-2 border-green-600 pl-5" data-id="element-1472" data-mp="34k6mqkamqohg">
                      <dt className="text-[12px] font-bold uppercase tracking-[0.1em] text-green-700" data-id="element-1473" data-mp="34lzj96rj3sbq">
                        Result
                      </dt>
                      <dd className="mt-1.5 font-display text-[19px] leading-snug text-ink" data-id="element-1474" data-mp="375e618nalnlj">
                        {item.outcome}
                      </dd>
                    </div>
                  </dl>
                </article>
              </Reveal>)}
          </div>
        </div>
      </section>

      <section className="bg-ink py-16 lg:py-24" aria-labelledby="data-heading" data-id="element-1475" data-mp="3c1uq9rrdfhtc">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1476" data-mp="3q25ioz8bx4gm">
          <SectionHeading eyebrow="Supporting data" title="The numbers behind the record" onDark data-id="element-1477" data-mp="3l4dpm1vy7cau" />
          <dl className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1478" data-mp="3n2xagv8slaw">
            {supportingData.map(item => <div key={item.label} className="border-t border-white/20 pt-5" data-id="element-1479" data-mp="3uw0ayr202rrb">
                <dt className="sr-only" data-id="element-1480" data-mp="cou0nf2b8g1l">{item.label}</dt>
                <dd data-id="element-1481" data-mp="8n8t898jotys">
                  <span className="block font-display text-[34px] leading-none text-gold-400" data-id="element-1482" data-mp="8nqlkus36o1f">
                    {item.value}
                  </span>
                  <span className="mt-3 block text-[15px] leading-snug text-white/70" data-id="element-1483" data-mp="r96h10f2ictc">
                    {item.label}
                  </span>
                </dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby="roles-heading" data-id="element-1484" data-mp="n8cw8fwx9e4o">
        <div className="mx-auto grid max-w-content gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-1485" data-mp="8jzxjb6g9h6h">
          <div className="lg:col-span-7" data-id="element-1486" data-mp="dbrsrz0jwb2y">
            <SectionHeading eyebrow="Major roles" title="Where the record was built" data-id="element-1487" data-mp="dcjbi991uebt" />
            <ol className="mt-8 border-l border-sand-300 pl-6" data-id="element-1488" data-mp="3vqivha4u53zu">
              {timeline.slice(1, 7).map(entry => <li key={entry.year} className="relative pb-8 last:pb-0" data-id="element-1489" data-mp="3jc8uw5p1cff">
                  <span aria-hidden className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-green-600" data-id="element-1490" data-mp="3lscr8lvk3lrt" />
                  <p className="font-display text-[18px] text-gold-600" data-id="element-1491" data-mp="1wapochehl4rr">{entry.year}</p>
                  <h3 className="mt-1 font-display text-[19px] leading-snug text-ink" data-id="element-1492" data-mp="1ri4wvmj1z3re">
                    {entry.title}
                  </h3>
                  <p className="mt-1.5 text-[15px] leading-relaxed text-slate-600" data-id="element-1493" data-mp="1nfi1jbn6b0pg">
                    {entry.description}
                  </p>
                </li>)}
            </ol>
          </div>
          <div className="lg:col-span-5" data-id="element-1494" data-mp="2f6fos06rtt3j">
            <div className="rounded-media border border-sand-200 bg-sand-100 p-7 lg:sticky lg:top-28" data-id="element-1495" data-mp="2b889zavvb1uz">
              <h2 className="font-display text-[22px] text-ink" data-id="element-1496" data-mp="2670z32yfhmk3">Evidence and documents</h2>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-600" data-id="element-1497" data-mp="217xvlmy3joe8">
                Every figure above comes from one of these. All are available for download in the
                media centre.
              </p>
              <ul className="mt-5 space-y-3" data-id="element-1498" data-mp="ps9a700hqxfj">
                {documents.map(document => <li key={document} data-id="element-1499" data-mp="frgnv5sgi2qg">
                    <a href="/media" className="flex items-start gap-2.5 text-[14px] leading-relaxed text-slate-700 transition-colors duration-150 ease-emphasis hover:text-green-700" data-id="element-1500" data-mp="kp02i6t2ghix">
                      <FileTextIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-green-600" data-id="element-1501" data-mp="uflaulyjl0f1" />
                      {document}
                    </a>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Seen the record? Read what comes next." description="The same discipline, applied nationally, with targets published before the work begins." primary={{
      label: 'Explore the agenda',
      to: '/agenda'
    }} secondary={{
      label: 'Check the facts',
      to: '/facts'
    }} data-id="element-1502" data-mp="18eb6ry9mku1t" />
    </>;
}